import { useEffect, useMemo, useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import debounce from 'lodash/debounce';

import {
  shouldShowWorkshopWarranty,
  WorkshopWarrantyExpirationDate,
} from '@/components/WorkshopWarrantyExpirationDate/WorkshopWarrantyExpirationDate';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAssociate from '@/icons/Associate.svg';
import IconDone from '@/icons/Done.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTools from '@/icons/Tools.svg';
import IconTrash from '@/icons/Trash.svg';
import IconUndo from '@/icons/Undo.svg';
import IconWarning from '@/icons/Warning.svg';
import {
  computeDuplicateActionNumber,
  getAdditionalInfoConfig,
  getPackActionTypeOrganizationWithRefashionStatus,
  getRefashionStatus,
  isCustom,
} from '@/models/actionType';
import {
  isActionMandatory,
  TEMPORARY_ID_PREFIX,
  useDeleteAction,
  useResetAction,
  useUpdateAction,
} from '@/models/article';
import { useGetDefectName } from '@/models/defectType';
import { type ArticleActionWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { type I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import ActionName from '../ActionName';
import { CreateEditActionDialog } from '../CreateEditActionDialog';
import PackActionName from '../PackActionName';

import { getEditedFields } from './getEditedFields';

const { element } = createBEMClasses('actions-list');

export const ActionCardHeader = ({
  action,
  previousAction,
  duplicateActionNumber,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  duplicateActionNumber?: number;
}) => {
  const { isMobile } = useViewPort();
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();

  const isActionEditable =
    !!action &&
    ((isCustom(action) && view.services.actions.fields.description.editable) ||
      (view.services.actions.fields.photo.shown && view.services.actions.fields.photo.editable) ||
      (view.services.actions.fields.context.shown &&
        view.services.actions.fields.context.editable) ||
      (view.services.actions.fields.associatedDefects.shown &&
        view.services.actions.fields.associatedDefects.editable) ||
      (!!getAdditionalInfoConfig(action)?.length &&
        view.services.actions.fields.additionalInfo.editable) ||
      (view.services.actions.fields.brandResponsibility.shown &&
        view.services.actions.fields.brandResponsibility.editable) ||
      (requestView.workshopPrice.shown && view.services.actions.fields.workshopPrice.editable) ||
      (requestView.organizationPrice.shown &&
        view.services.actions.fields.organizationPrice.editable) ||
      (view.services.actions.fields.spareParts.shown &&
        (view.services.actions.fields.spareParts.canAddRemoveSparePart ||
          view.services.actions.fields.spareParts.quantity.editable)));

  const isMandatoryAction = action && isActionMandatory(action);

  const isActionUndeletable =
    !action &&
    !!previousAction &&
    !previousAction.parents.length &&
    view.services.actions.canRemoveAction;

  const showEditButton = !isMandatoryAction && isActionEditable && isMobile;
  const showResetButton =
    !isMandatoryAction && (isActionUndeletable || (!!previousAction && isActionEditable));
  const showDeleteButton =
    !isMandatoryAction &&
    !!action &&
    !action?.parents.length &&
    view.services.actions.canRemoveAction;

  return (
    <Stack
      row
      gap="1rem"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="nowrap"
      padding="12px"
      className={element('card-name', {
        edited: getEditedFields({ action, previousAction }).isNameEdited,
        done: !!action?.doneByWorkshop,
      })}
    >
      <ActionCardName
        action={action}
        previousAction={previousAction}
        duplicateActionNumber={duplicateActionNumber}
      />
      {(showEditButton || showResetButton || showDeleteButton) && (
        <Stack row gap="0.5rem" alignItems="center" flexWrap="nowrap">
          {showEditButton && <EditActionButton action={action} previousAction={previousAction} />}
          {showResetButton && <ResetActionButton action={action} previousAction={previousAction} />}
          {showDeleteButton && <DeleteActionButton action={action} />}
        </Stack>
      )}
    </Stack>
  );
};

const ActionCardName = ({
  action,
  previousAction,
  duplicateActionNumber,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
  duplicateActionNumber?: number;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { errors, view, iam, article } = useArticleContext();

  const actionOrPreviousAction = action ?? previousAction!;

  const refashionStatus = iam.hasOnlyWorkshopScopeOnArticle
    ? undefined
    : getRefashionStatus(actionOrPreviousAction.organizationPrice);

  const packActionType = iam.hasOnlyWorkshopScopeOnArticle
    ? actionOrPreviousAction.packActionTypeOrganization
    : getPackActionTypeOrganizationWithRefashionStatus(
        actionOrPreviousAction,
        actionOrPreviousAction.organizationPrice
      );

  const isMissingDescription = !!errors.services?.actions?.actions?.find(
    (act) => act.id === action?.id
  )?.missingDescription;

  const trimmedCustomDescription = (actionOrPreviousAction.customDescription ?? '-')?.trim();

  const showInput =
    !!action &&
    isCustom(action) &&
    view.services.actions.fields.description.editable &&
    !action.id.startsWith(TEMPORARY_ID_PREFIX) &&
    !isMobile;

  const doneByWorkshopName = action?.doneByWorkshop?.name;
  const doneByWorkshopLabel = doneByWorkshopName
    ? t({ id: 'actions-table.name.done-by', message: `Done by ${doneByWorkshopName}` })
    : undefined;

  const showWorkshopWarrantyExpirationDate =
    !!action && shouldShowWorkshopWarranty(article.snapshot.articleActions);

  return (
    <Stack row alignItems="flex-start" gap="0.5rem" flexWrap="nowrap" style={{ flex: 1 }}>
      {action?.doneByWorkshop ? (
        <IconDone style={{ fontSize: '1.50rem' }} className="color-primary-700" />
      ) : (
        <IconTools style={{ fontSize: '1.50rem', paddingTop: showInput ? 0 : '0.125rem' }} />
      )}

      <Stack style={{ flex: 1 }}>
        <Stack row gap="0.5rem">
          {showInput ? (
            <ActionCustomDescriptionInput action={action} />
          ) : (
            <>
              {!!actionOrPreviousAction.actionTypeOrganization && (
                <ActionName
                  actionType={actionOrPreviousAction.actionTypeOrganization}
                  refashionStatus={refashionStatus}
                  duplicateActionNumber={duplicateActionNumber}
                  weight="medium"
                  disabled={!action}
                />
              )}

              {!!packActionType && (
                <PackActionName
                  packActionType={packActionType}
                  duplicateActionNumber={duplicateActionNumber}
                  disabled={!action}
                />
              )}

              {isCustom(actionOrPreviousAction) && (
                <Paragraph
                  weight="medium"
                  color={!action ? 'disabled' : undefined}
                  style={isMissingDescription ? { color: 'var(--color-danger-700)' } : undefined}
                >
                  {trimmedCustomDescription}
                  {!!duplicateActionNumber && `#${duplicateActionNumber}`}
                </Paragraph>
              )}

              {!isMobile && <LinkedMandatoryBadges action={action} />}
            </>
          )}
        </Stack>

        {isMobile && <LinkedMandatoryBadges action={action} />}

        {isMobile && showWorkshopWarrantyExpirationDate && (
          <WorkshopWarrantyExpirationDate
            workshopWarrantyExpirationDate={action?.workshopWarrantyExpirationDate ?? null}
            showIcon={false}
          />
        )}

        {(!!doneByWorkshopLabel || (!isMobile && showWorkshopWarrantyExpirationDate)) && (
          <Stack row gap="0.25rem">
            {!!doneByWorkshopLabel && (
              <Paragraph size="200" color="secondary">
                {doneByWorkshopLabel}
              </Paragraph>
            )}
            {!isMobile && !!doneByWorkshopLabel && showWorkshopWarrantyExpirationDate && (
              <Paragraph size="200" color="secondary">
                •
              </Paragraph>
            )}
            {!isMobile && showWorkshopWarrantyExpirationDate && (
              <WorkshopWarrantyExpirationDate
                workshopWarrantyExpirationDate={action?.workshopWarrantyExpirationDate ?? null}
              />
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export const ActionCustomDescriptionInput = ({
  action,
}: {
  action: ArticleActionWithRelations;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { request, article, errors } = useArticleContext();

  const { mutateAsync: updateAction } = useUpdateAction({
    requestId: request.id,
    articleId: article.id,
  });

  const debouncedUpdateAction = useMemo(() => {
    return debounce(updateAction, 500);
  }, [updateAction]);

  const [customDescription, setCustomDescription] = useState(action.customDescription ?? '');

  useEffect(() => {
    setCustomDescription(action.customDescription ?? '');
    debouncedUpdateAction.cancel();
  }, [action.customDescription, debouncedUpdateAction]);

  const isMissingDescription = !!errors.services?.actions?.actions?.find(
    (act) => action?.id === act.id
  )?.missingDescription;

  const label = t({
    id: 'actions-table.custom-description-input.label',
    message: 'Description of custom action',
  });

  return (
    <InputText
      label={isMobile ? label : undefined}
      aria-label={isMobile ? undefined : label}
      placeholder={t({
        id: 'actions-table.custom-description-input.placeholder',
        message: 'Add a description...',
      })}
      onChange={(value) => {
        setCustomDescription(value);
        debouncedUpdateAction({
          actionId: action.id,
          body: {
            description: value.trim(),
          },
        });
      }}
      value={customDescription}
      isInvalid={isMissingDescription}
      style={{ flex: 1 }}
    />
  );
};

const EditActionButton = ({
  action,
  previousAction,
}: {
  action: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { t } = useLingui();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        variant="secondary"
        size="medium"
        iconOnly
        tooltip={t({ id: 'actions-table.edit', message: 'Edit' })}
        ariaLabel={t({ id: 'actions-table.edit', message: 'Edit' })}
        disabled={action.id.startsWith(TEMPORARY_ID_PREFIX)}
        onPress={() => setIsOpen(true)}
      >
        <IconEdit />
      </Button>
      <CreateEditActionDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        action={action}
        previousAction={previousAction}
        mode="edit"
      />
    </>
  );
};

const ResetActionButton = ({
  action,
  previousAction,
}: {
  action?: ArticleActionWithRelations;
  previousAction?: ArticleActionWithRelations;
}) => {
  const { article, request, state } = useArticleContext();
  const { t } = useLingui();

  const { mutateAsync: resetAction, isPending: isResetActionPending } = useResetAction({
    requestId: request.id,
    articleId: article.id,
  });

  return (
    <Button
      variant="secondary"
      size="medium"
      iconOnly
      tooltip={t({ id: 'actions-table.undo', message: 'Undo changes' })}
      ariaLabel={t({ id: 'actions-table.undo', message: 'Undo changes' })}
      disabled={action?.id.startsWith(TEMPORARY_ID_PREFIX)}
      isLoading={isResetActionPending}
      onPress={() => {
        resetAction((action ?? previousAction)!.stableId);
        state.priceUpdates.setUpdatesInProgress((prevState) =>
          prevState.filter((priceUpdate) => priceUpdate.actionId !== action?.id)
        );
      }}
    >
      <IconUndo />
    </Button>
  );
};

const DeleteActionButton = ({ action }: { action: ArticleActionWithRelations }) => {
  const { article, request, state } = useArticleContext();
  const { t } = useLingui();

  const isLastNonChildAction =
    article.step?.step === 'validation' &&
    article.snapshot.articleActions.filter((articleAction) => !articleAction.parents.length)
      .length === 1;

  const { mutate: deleteAction } = useDeleteAction({
    articleId: article.id,
    requestId: request.id,
  });

  const tooltip = isLastNonChildAction
    ? t({
        id: 'actions-table.delete.last-action-tooltip',
        message:
          "At least one action must remain in the proposal. To refuse all actions, please click the 'Client Refusal' button",
      })
    : t({ id: 'action-tables.action.deletion', message: 'Remove' });

  return (
    <Button
      variant="secondary"
      size="medium"
      iconOnly
      tooltip={tooltip}
      ariaLabel={t({ id: 'action-tables.action.deletion', message: 'Remove' })}
      disabled={action.id.startsWith(TEMPORARY_ID_PREFIX) || isLastNonChildAction}
      onPress={() => {
        if (
          action.media.length > 0 &&
          !confirm(
            t({
              id: 'action-tables.action.confirm-deletion',
              message:
                'Are you sure you want to delete this action? The associated photos will be deleted as well.',
            })
          )
        ) {
          return;
        }

        deleteAction(action.id);
        state.priceUpdates.setUpdatesInProgress((prevState) =>
          prevState.filter((priceUpdate) => priceUpdate.actionId !== action.id)
        );
      }}
    >
      <IconTrash />
    </Button>
  );
};

const LinkedMandatoryBadges = ({ action }: { action?: ArticleActionWithRelations }) => {
  const { _db } = useDatabaseTranslation();
  const { article } = useArticleContext();
  const getDefectName = useGetDefectName();
  const parentActionNamesFormatted = action
    ? computeParentActionNames(action, article.snapshot.articleActions, _db)
    : undefined;

  const linkedMandatoryDefects = article.snapshot.articleDefects.filter(
    (articleDefect) =>
      !!action?.articleActionToArticleDefects.find(
        ({ articleDefectId, mandatory }) => mandatory && articleDefectId === articleDefect.id
      )
  );

  const showLinkedBadge = !!action?.parents.length;
  const showMandatoryBadge = !!linkedMandatoryDefects?.length;

  if (!showLinkedBadge && !showMandatoryBadge) {
    return null;
  }

  const defectsName = linkedMandatoryDefects
    ?.map((linkedMandatoryDefect) => getDefectName(linkedMandatoryDefect))
    .join(',');

  const linkedMandatoryDefectsNumber = linkedMandatoryDefects.length;

  return (
    <Stack row gap="0.5rem">
      {showLinkedBadge && (
        <Tooltip
          content={
            <Trans id="actions-table.name.child-action-association">
              This action is associated with{' '}
              <Plural
                value={action?.parents.length}
                one={`the ${parentActionNamesFormatted} action`}
                other={`${parentActionNamesFormatted} actions`}
              />{' '}
              and cannot be deleted independently
            </Trans>
          }
        >
          <Button variant="style-less" size="medium">
            <Badge color="secondary" hasBorder size="small" borderRadius="soft">
              <IconAssociate className="color-secondary-700" />
              <Trans id="actions-table.name.child-action-association-title">Linked</Trans>
            </Badge>
          </Button>
        </Tooltip>
      )}
      {showMandatoryBadge && (
        <Tooltip
          content={
            <Trans id="actions-table.name.mandatory-association">
              Delete{' '}
              <Plural
                value={linkedMandatoryDefectsNumber}
                one={`the "${defectsName}" defect`}
                other={`${defectsName} defects`}
              />{' '}
              to remove this action
            </Trans>
          }
        >
          <Button variant="style-less" size="medium">
            <Badge color="warning" hasBorder size="small" borderRadius="soft">
              <IconWarning className="color-warning" />
              <Trans id="actions-table.name.mandatory-action-association-title">Mandatory</Trans>
            </Badge>
          </Button>
        </Tooltip>
      )}
    </Stack>
  );
};

/**
 *
 * @param action the action potentially having parents
 * @param articleActions the whole list of actions in the article
 * @param _db translation tool
 * @param parentDuplicateActionNumber
 * @returns the list of parent actions names formatted
 * Example: "Clasp earring replacement", "Missing stone", "Chain shortening"
 */
const computeParentActionNames = (
  action: { parents: { id: string }[] },
  articleActions: ArticleActionWithRelations[],
  _db: (translation: I18nWording) => string
) => {
  const actionParentIds = action.parents.map((parentAction) => parentAction.id);
  const parentActions = actionParentIds
    ? articleActions.filter((articleAction) => actionParentIds.includes(articleAction.id))
    : undefined;
  const parentActionNames = parentActions?.length
    ? parentActions.map((parentAction) => {
        const parentDuplicateActionNumber = computeDuplicateActionNumber(
          parentAction,
          parentActions
        );

        return parentAction.actionTypeOrganization
          ? `"${_db(parentAction.actionTypeOrganization.name)}${parentDuplicateActionNumber ? ` #${parentDuplicateActionNumber}` : ''}"`
          : undefined;
      })
    : undefined;

  return parentActionNames?.join(', ');
};
